// @flow

import { batchActions } from 'redux-batched-actions';
import { routeTo } from 'redux-router-kit';

import { changeTypes, intervalTypes } from '@zapier/billing/src/types';

import * as Account from 'app/entities/Account';
import * as Offer from 'app/entities/Offer';
import * as Plan from 'app/entities/Plan';
import * as ToastNotification from 'app/entities/ToastNotification';
import ActionTypes from 'app/billing/ActionTypes';
import DashboardActions from 'app/dashboard/DashboardActions';
import FlowActions from 'app/beta-editor/FlowActions';
import TrackActions from 'app/track/TrackActions';
import { addParamsToUrl } from 'app/common/utils/UrlUtils';
import { currentUrl } from 'app/common/utils/RouterSelectors';
import { fetchJson } from 'app/common/fetch';
import { selectPlanId } from 'app/billing/Selectors';
import {
  buildDowngradeOfferUrl,
  buildDowngradeTeamWarningUrl,
  buildDowngradeWarningUrl,
  buildPlanChangeUrl,
  formatPlanDowngradeWhy,
} from 'app/billing/utils';

import type {
  ChangeType,
  Currency,
  Interval,
  Plan as PlanType,
  QuestionProps,
} from '@zapier/billing/src/types';

import type { Dispatch, GetState } from 'app/common/types';

const BillingActions = {
  changePage(newOffset: number) {
    return {
      newOffset,
      type: ActionTypes.CHANGE_PAGE,
    };
  },

  selectBillingInterval: (planName: ?string, interval: ?string) => (
    dispatch: Dispatch,
    getState: GetState
  ) => {
    return dispatch({
      planName,
      interval,
      routeTo: addParamsToUrl(currentUrl(getState()), { interval }),
      type: ActionTypes.SELECT_BILLING_INTERVAL,
    });
  },

  upgradePlan(
    planName: string,
    interval: Interval,
    { shouldNotLoadZaps }: { shouldNotLoadZaps: boolean }
  ) {
    const batchedActions = batchActions([BillingActions.setInFlight(false)]);

    return (dispatch: Dispatch) => {
      return dispatch(BillingActions.changePlan(planName, interval)).then(
        () => {
          if (shouldNotLoadZaps) {
            return dispatch(batchedActions);
          }
          return dispatch(FlowActions.loadZaps()).then(() => {
            return dispatch(batchedActions);
          });
        }
      );
    };
  },

  confirmPlanChange(newAccountId) {
    return { type: ActionTypes.CONFIRM_PLAN_CHANGE, newAccountId };
  },

  reloadZapsAndConfirmPlanChange() {
    return (dispatch: Dispatch) => {
      dispatch(FlowActions.loadZaps()).then(() => {
        dispatch(
          batchActions([
            BillingActions.setInFlight(false),
            BillingActions.confirmPlanChange(),
          ])
        );
      });
    };
  },

  acceptOffer(offerCode: string) {
    return (dispatch: Dispatch) => {
      dispatch(BillingActions.setInFlight(true));
      dispatch(Offer.actions.acceptOffer(offerCode))
        .then(() => dispatch(BillingActions.setInFlight(false)))
        .catch(() => dispatch(BillingActions.setInFlight(false)));
    };
  },

  confirmDowngradePlan(
    fromPlanName: string,
    toPlanName: string,
    interval: Interval
  ) {
    return (dispatch: Dispatch) => {
      dispatch(BillingActions.setInFlight(false));
      dispatch({
        type: ActionTypes.CONFIRM_DOWNGRADE_PLAN,
        routeTo: buildDowngradeWarningUrl(fromPlanName, toPlanName, interval),
      });
    };
  },

  goToPlanInvervalChange() {
    return (dispatch: Dispatch, getState: GetState) => {
      const state = getState();
      const currentAccount = Account.selectors.all.currentAccount(state);
      const planName = encodeURIComponent(
        Account.selectors.planName(currentAccount)
      );
      dispatch({
        type: ActionTypes.GO_TO_PLAN_INTERVAL_CHANGE,
        routeTo: `/app/billing/plans/change/${planName}`,
      });
    };
  },

  goToUpgradeToTeams() {
    return (dispatch: Dispatch, getState: GetState) => {
      return dispatch(Account.actions.loadCollection())
        .then(() => dispatch(Plan.actions.loadCollection()))
        .then(() => {
          const state = getState();
          const teamPlan = Plan.selectors.all.findTeamPlan(state);
          const currentPlan = Plan.selectors.all.currentPlan(state);
          // Note that this uses `Account` to determine whether
          // the current account is a team account _instead of_
          // `currentPlan` because team _members_ don't have access
          // to plans and therefore would not have any plans data
          // to look at, and `currentPlan` would not exist.
          const isAlreadyTeamPlan = Account.selectors.isTeam(
            Account.selectors.all.currentAccount(state)
          );

          if (isAlreadyTeamPlan) {
            return dispatch(routeTo('/app/settings/team'));
          }

          // This account's plan group doesn't have a team plan. Super edge case, so let's just route to the contact form
          if (!teamPlan) {
            dispatch(
              ToastNotification.actions.createFailure(
                'Sorry, your account doesn’t currently support teams. Please contact our support team for assistance!'
              )
            );

            return dispatch(routeTo('/app/contact-us'));
          }

          const fromPlanName = Plan.selectors.name(currentPlan);
          const teamPlanName = Plan.selectors.name(teamPlan);

          return dispatch({
            type: ActionTypes.GO_TO_UPGRADE_TO_TEAMS,
            routeTo: buildPlanChangeUrl('upgrade', fromPlanName, teamPlanName),
          });
        });
    };
  },

  recordDowngradeSurvey(
    planName: string,
    interval: Interval,
    questions: Array<QuestionProps>,
    comment: string
  ) {
    const why = formatPlanDowngradeWhy(questions, comment);

    return (dispatch: Dispatch, getState: GetState) => {
      const state = getState();
      const accountId = Account.selectors.all.currentAccountId(state);
      if (why) {
        dispatch(
          TrackActions.track('Billing: downgrade survey', {
            accountId,
            reason: why,
          })
        );
      }
    };
  },

  downgradePlan(
    planName: string,
    interval: Interval,
    questions?: Array<QuestionProps>,
    comment?: string
  ) {
    const why =
      questions || comment
        ? formatPlanDowngradeWhy(questions || [], comment)
        : undefined;
    return (dispatch: Dispatch) => {
      return dispatch(BillingActions.changePlan(planName, interval, why))
        .then(() => {
          return dispatch(BillingActions.reloadZapsAndConfirmPlanChange());
        })
        .catch(error => {
          return dispatch(BillingActions.setChangePlanError(error.message));
        });
    };
  },

  changePlan(planName: string, intervalName: Interval, why: ?string) {
    return (dispatch: Dispatch, getState: GetState) => {
      const state = getState();
      const accountId = Account.selectors.all.currentAccountId(state);
      const planId = selectPlanId(state, { planName, intervalName });
      const accountPlan = Plan.selectors.all.entity(planId, state);
      const planData = {
        accountPlan: Plan.selectors.data(accountPlan),
        plan_id: planId,
        why,
      };

      const updateActions = [
        BillingActions.setInFlight(true),
        Account.actions.updateEntityData(planData, accountId),
      ];
      dispatch(batchActions(updateActions));

      const messages = {
        failure: 'There was an error saving your plan. Please try again.',
        shouldNotify: false,
      };
      const saveEntityAction = Account.actions.saveEntity(messages, accountId);

      return dispatch(saveEntityAction).then(() => {
        const currentAccount = Account.selectors.all.currentAccount(getState());
        const isFailure = Account.selectors.isFailure(currentAccount);
        if (isFailure) {
          const [statusMessage] = Account.selectors.statusMessages(
            currentAccount
          );

          dispatch(BillingActions.setInFlight(false));
          throw new Error(statusMessage);
        } else {
          dispatch(DashboardActions.loadHeldReasons());

          dispatch(
            TrackActions.track('Billing: plan changed', {
              accountId,
              plan: planName,
              planId,
              interval: intervalName,
            })
          );
        }
      });
    };
  },

  setChangePlanError(message: string | null) {
    return (dispatch: Dispatch) => {
      dispatch({ type: ActionTypes.CHANGE_PLAN_ERROR, message });
    };
  },

  createNewAccount(planName: string, intervalName: Interval) {
    return (dispatch: Dispatch, getState: GetState) => {
      dispatch(BillingActions.setInFlight(true));
      const newAccountTempId = 'NEW_ACCOUNT';
      const planId = selectPlanId(getState(), { planName, intervalName });
      const currentAccount = Account.selectors.all.currentAccount(getState());

      dispatch(
        Account.actions.createCollectionUiEntity({
          id: newAccountTempId,
          plan_id: planId,
          plan_group_id: Account.selectors.planGroupId(currentAccount),
        })
      );
      const newAccountCid = Account.selectors.cid(
        Account.selectors.all.entity(newAccountTempId, getState())
      );

      dispatch(
        Account.actions.createEntity(
          {
            failure: 'Sorry, but there was an error creating your account',
            shouldNotify: true,
          },
          newAccountTempId
        )
      ).then(() => {
        const newAccountEntity = Account.selectors.all.entityByCid(
          newAccountCid,
          getState()
        );
        const newAccountId = Account.selectors.id(newAccountEntity);
        dispatch(BillingActions.setInFlight(false));
        if (Account.selectors.isSuccess(newAccountEntity)) {
          dispatch(BillingActions.confirmPlanChange(newAccountId));
        }
      });
    };
  },

  setInFlight(isInFlight: boolean) {
    return { type: ActionTypes.SET_IN_FLIGHT, isInFlight };
  },

  // leave plans page, clear out some state in the process
  onLeave(path: ?string, shouldRefresh: ?boolean = false) {
    return {
      type: ActionTypes.ON_LEAVE,
      routeTo: path ? { path, shouldRefresh } : undefined,
    };
  },

  toggleDowngradeUnderstood() {
    return {
      type: ActionTypes.TOGGLE_DOWNGRADE_UNDERSTOOD,
    };
  },

  showDowngradeSurvey(
    fromPlanName: string,
    toPlanName: string,
    interval: Interval
  ) {
    fromPlanName = encodeURIComponent(fromPlanName);
    toPlanName = encodeURIComponent(toPlanName);
    return {
      type: ActionTypes.SHOW_DOWNGRADE_SURVEY,
      routeTo: `/app/billing/plans/downgrade-survey/${fromPlanName}/${toPlanName}/${interval.toLowerCase()}`,
    };
  },

  showDowngradeOffer(
    fromPlanName: string,
    toPlanName: string,
    interval: Interval,
    offerCode: string
  ) {
    return {
      type: ActionTypes.SHOW_DOWNGRADE_OFFER,
      routeTo: buildDowngradeOfferUrl(
        fromPlanName,
        toPlanName,
        interval,
        offerCode
      ),
    };
  },

  cancelChangePlan(
    fromPlanName: string,
    toPlanName: string,
    interval: Interval,
    changeType: ChangeType,
    route: string = '/app/billing/plans'
  ) {
    return (dispatch: Dispatch, getState: GetState) => {
      const accountId = Account.selectors.all.currentAccountId(getState());

      dispatch(
        TrackActions.track('Billing: cancelChangePlan', {
          accountId,
          fromPlanName,
          toPlanName,
          interval,
          changeType,
          step: 'first step',
        })
      );

      return dispatch({
        type: ActionTypes.CANCEL_EDIT_CARD,
        routeTo: route,
      });
    };
  },

  goToDashboard() {
    return (dispatch: Dispatch) => {
      return dispatch({
        type: ActionTypes.GO_TO_DASHBOARD,
        routeTo: '/app/zaps',
      });
    };
  },

  goToMemberHome() {
    return (dispatch: Dispatch) => {
      return dispatch({
        type: ActionTypes.GO_TO_MEMBER_HOME,
        routeTo: '/app/dashboard',
      });
    };
  },

  cancelDowngrade(
    fromPlanName: string,
    toPlanName: string,
    interval: Interval,
    step: string
  ) {
    return (dispatch: Dispatch, getState: GetState) => {
      const accountId = Account.selectors.all.currentAccountId(getState());
      dispatch(
        TrackActions.track('Billing: cancel downgrade', {
          accountId,
          fromPlanName,
          plan: toPlanName,
          interval,
          step,
        })
      );
      return dispatch({
        type: ActionTypes.CANCEL_DOWNGRADE,
        routeTo: '/app/zaps',
      });
    };
  },

  selectPlan(
    plan: PlanType,
    changeType: ChangeType,
    isFall2019Pricing: boolean
  ) {
    return (dispatch: Dispatch, getState: GetState) => {
      const currentPlan = Plan.selectors.all.currentPlan(getState());
      const fromPlanName = Plan.selectors.name(currentPlan).toLowerCase();
      const toPlanName = isFall2019Pricing
        ? plan.selectedTaskLimitVariant.name.toLowerCase()
        : plan.name.toLowerCase();
      const isFreePlan = plan.billingAmount === 0;

      // Three cases here:
      // 1. When upgrading from free plan, default to yearly interval
      // 2. When downgrading to free plan, default to monthly interval
      // 3. Otherwise, default to the current plan's interval
      const selectedInterval =
        Plan.selectors.amount(currentPlan) === 0
          ? intervalTypes.year
          : isFreePlan
          ? intervalTypes.month
          : Plan.selectors.interval(currentPlan);

      if (
        changeType === changeTypes.downgrade &&
        Plan.selectors.hasTeams(currentPlan)
      ) {
        return dispatch(
          BillingActions.onLeave(
            buildDowngradeTeamWarningUrl(
              fromPlanName,
              toPlanName,
              selectedInterval
            )
          )
        );
      }

      if (isFreePlan) {
        if (changeType === changeTypes.downgrade) {
          return dispatch(
            BillingActions.confirmDowngradePlan(
              fromPlanName,
              toPlanName,
              selectedInterval
            )
          );
        }
        if (changeType === changeTypes.change) {
          return dispatch(
            BillingActions.onLeave('/app/settings/billing?section=card')
          );
        }
      }

      const url = buildPlanChangeUrl(
        changeType,
        fromPlanName,
        toPlanName,
        selectedInterval
      );
      return dispatch(BillingActions.onLeave(url));
    };
  },

  selectCurrency(currency: Currency) {
    return {
      currency,
      type: ActionTypes.SELECT_CURRENCY,
    };
  },

  loadCurrencyRates() {
    return async (dispatch: Dispatch) => {
      dispatch({ type: ActionTypes.GET_CURRENCY_RATES });
      try {
        // Get current rates with USD as base for multiplier
        // $FlowFixMe
        const { objects: rateList } = await fetchJson(
          `/api/v4/currencies/rates/`
        );
        // Convert rate list to a dictionary for easy look-up
        const rates = rateList.reduce(
          (acc, rate) => ({ ...acc, [rate.name]: rate }),
          {}
        );
        // Apply multiplier to available currencies
        dispatch({
          data: rates,
          type: ActionTypes.GET_CURRENCY_RATES_DONE,
        });
      } catch (error) {
        dispatch({
          error: `Rates API: ${error.message}`,
          messages: error,
          type: ActionTypes.GET_CURRENCY_RATES_FAIL,
        });
      }
    };
  },
};

export default BillingActions;
